import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 130507
const CHECKSUM_OFFSET = 0x1fdca
const FLAG_START = 0x280a
const CHANNEL_START = 0x2d4a
const CHANNEL_SIZE = 32
const CHANNEL_COUNT = 900
const CHUNK_SIZE = 0x20
const MODES = ['FM', 'AM', 'WFM']
const DUPLEX = ['', '-', '+', 'split']
const STEPS = [5, 6.25, 8.33, 10, 12.5, 15, 20, 25, 50, 100, 9]
const POWER_LEVELS = ['Hi', 'L3', 'L2', 'L1']
const CHARSET =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz.,:;*#_-/&()@!?^ '

type FT2DSpec = {
  id: string
  model: string
  summary: string
  modelIds: readonly string[]
}

export type FT2DProfile = {
  protocol: 'yaesu-ft2d'
  id: string
  vendor: 'Yaesu'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 38400
  channelMinimum: 1
  channelMaximum: number
  nameLength: 16
  memorySize: number
  modelIds: readonly string[]
  powerLevels: readonly string[]
  modes: readonly string[]
}

const SPECS: readonly FT2DSpec[] = [
  {
    id: 'yaesu-ft-1d',
    model: 'FT-1D',
    summary: 'Yaesu FT-1D C4FM/analog handheld.',
    modelIds: ['AH44M'],
  },
  {
    id: 'yaesu-ft2d',
    model: 'FT2D',
    summary: 'Yaesu FT2D C4FM/analog handheld.',
    modelIds: ['AH60M', 'AH60G'],
  },
  {
    id: 'yaesu-ft3d',
    model: 'FT3D',
    summary: 'Yaesu FT3D C4FM/analog handheld.',
    modelIds: ['AH72M'],
  },
  {
    id: 'yaesu-ft5d',
    model: 'FT5D',
    summary: 'Yaesu FT5D C4FM/analog handheld.',
    modelIds: ['AH82M'],
  },
]

export const FT2D_PROFILES: readonly FT2DProfile[] = SPECS.map((spec) => ({
  protocol: 'yaesu-ft2d',
  id: spec.id,
  vendor: 'Yaesu',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '900 channels', '16-character names'],
  baudRate: 38400,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 16,
  memorySize: MEMORY_SIZE,
  modelIds: spec.modelIds,
  powerLevels: POWER_LEVELS,
  modes: ['FM', 'NFM', 'AM', 'WFM', 'DN'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Hi',
    skip: false,
  }
}

function decodeBbcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = length - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function decodeLabel(raw: Uint8Array): string {
  let text = ''
  for (const value of raw) {
    if (value === 0xff || value >= CHARSET.length) break
    text += CHARSET[value]
  }
  return text.trim()
}

export function parseFT2DChannel(
  image: Uint8Array,
  profile: FT2DProfile,
  number: number,
): WebChannel {
  const flag = image[FLAG_START + number - 1]
  if ((flag & 0x02) === 0 || (flag & 0x01) === 0) return emptyChannel(number)
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxFrequency = decodeBbcd(raw.slice(2, 5)) * 1000
  if (!rxFrequency) return emptyChannel(number)
  const offset = decodeBbcd(raw.slice(24, 27)) * 1000
  const duplex = (raw[1] >> 4) & 0x03
  const txFrequency =
    duplex === 1
      ? rxFrequency - offset
      : duplex === 2
        ? rxFrequency + offset
        : duplex === 3
          ? offset
          : rxFrequency
  let mode = MODES[raw[1] >> 6] ?? 'FM'
  if (raw[5] & 0x30) mode = 'DN'
  else if (mode === 'FM' && raw[0] & 0x20) mode = 'NFM'
  const toneMode = ['', 'Tone', 'TSQL', 'DTCS'][raw[5] & 0x0f] ?? ''
  return {
    number,
    empty: false,
    name: decodeLabel(raw.slice(8, 24)),
    rxFrequency,
    txFrequency,
    toneMode: mode === 'DN' ? 'none' : toneMode === 'DTCS' ? 'dtcs' : toneMode ? 'ctcss' : 'none',
    ctcss: mode === 'DN' || toneMode === 'DTCS' ? null : CTCSS_TONES[raw[27]] ?? null,
    dtcs: mode === 'DN' || toneMode !== 'DTCS' ? null : DTCS_CODES[raw[28]] ?? null,
    dtcsPolarity: 'N',
    mode,
    power: POWER_LEVELS[3 - ((raw[5] >> 6) & 0x03)] ?? 'Hi',
    skip: (flag & 0x0c) !== 0,
    tuningStep: STEPS[raw[1] & 0x0f] ?? 5,
  }
}

export function parseFT2DChannels(
  image: Uint8Array,
  profile: FT2DProfile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseFT2DChannel(image, profile, index + 1),
  )
}

export function applyFT2DChannel(
  image: Uint8Array,
  profile: FT2DProfile,
  channel: WebChannel,
): void {
  const flagOffset = FLAG_START + channel.number - 1
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0)
    raw[0] = 0x14
    image[flagOffset] &= ~0x02
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0)) raw.fill(0)
  image[flagOffset] |= 0x03
  const tx = channel.txFrequency ?? channel.rxFrequency
  const split = Math.abs(tx - channel.rxFrequency) > 70_000_000
  const duplex = split ? 3 : tx > channel.rxFrequency ? 2 : tx < channel.rxFrequency ? 1 : 0
  const mode =
    channel.mode === 'NFM'
      ? 'FM'
      : channel.mode === 'DN'
        ? 'FM'
        : channel.mode
  raw[0] =
    (raw[0] & 0xdf) |
    (channel.mode === 'NFM' ? 0x20 : 0)
  raw[1] =
    ((Math.max(0, MODES.indexOf(mode)) & 0x03) << 6) |
    (duplex << 4) |
    (Math.max(0, STEPS.indexOf(channel.tuningStep ?? 5)) & 0x0f)
  encodeBbcd(Math.round(channel.rxFrequency / 1000), 3).forEach((value, index) => {
    raw[2 + index] = value
  })
  const powerIndex = 3 - Math.max(0, profile.powerLevels.indexOf(channel.power))
  const toneMode =
    channel.toneMode === 'dtcs' ? 3 : channel.toneMode === 'ctcss' ? 1 : 0
  raw[5] =
    ((powerIndex & 0x03) << 6) |
    (channel.mode === 'DN' ? 0x10 : 0) |
    (toneMode & 0x0f)
  raw[6] = 0
  raw[7] = 0
  raw[27] = Math.max(
    0,
    (CTCSS_TONES as readonly number[]).indexOf(
      channel.ctcss ?? CTCSS_TONES[0],
    ),
  )
  raw[28] = Math.max(
    0,
    (DTCS_CODES as readonly number[]).indexOf(
      channel.dtcs ?? DTCS_CODES[0],
    ),
  )
  encodeBbcd(
    Math.round((split ? tx : Math.abs(tx - channel.rxFrequency)) / 1000),
    3,
  ).forEach((value, index) => {
    raw[24 + index] = value
  })
  image.fill(0xff, start + 8, start + 24)
  for (let index = 0; index < 16; index += 1) {
    const character = channel.name[index] ?? ' '
    const encoded = CHARSET.indexOf(character)
    raw[8 + index] = encoded >= 0 ? encoded : CHARSET.indexOf(' ')
  }
  image[flagOffset] =
    (image[flagOffset] & 0xf0) |
    (channel.skip ? 0x04 : 0) |
    0x03
}

function checksum(image: Uint8Array): number {
  let total = 0
  for (let index = 0; index < CHECKSUM_OFFSET; index += 1) {
    total = (total + image[index]) & 0xff
  }
  return total
}

export async function readFT2DRadioWithTransport(
  profile: FT2DProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  let identity = await transport.readExact(10, 4000)
  if (identity[0] === ACK) identity = await transport.readExact(10, 4000)
  await transport.write(Uint8Array.of(ACK))
  const image = new Uint8Array(MEMORY_SIZE)
  image.set(identity)
  image.set(await transport.readExact(MEMORY_SIZE - 10, 12000), 10)
  onProgress?.({
    current: MEMORY_SIZE,
    total: MEMORY_SIZE,
    label: 'Reading Yaesu FT2D memory',
  })
  const model = new TextDecoder().decode(image.slice(0, 5))
  if (!profile.modelIds.includes(model)) {
    throw new Error(`The radio identifies as ${model}, not ${profile.model}.`)
  }
  if (image[CHECKSUM_OFFSET] !== checksum(image)) {
    throw new Error('FT2D image checksum failed.')
  }
  return { image, reportedModel: profile.model }
}

export async function writeFT2DRadioWithTransport(
  profile: FT2DProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than FT2D memory.')
  }
  await transport.discardInput()
  image[CHECKSUM_OFFSET] = checksum(image)
  const output = image
  await transport.write(output.slice(0, 10))
  if ((await transport.readExact(1, 3500))[0] !== ACK) {
    throw new Error('FT2D rejected the clone header.')
  }
  for (let offset = 10; offset < MEMORY_SIZE; offset += CHUNK_SIZE) {
    await transport.write(output.slice(offset, offset + CHUNK_SIZE))
    onProgress?.({
      current: offset + CHUNK_SIZE,
      total: MEMORY_SIZE,
      label: 'Writing Yaesu FT2D memory',
    })
  }
}
